import {
  Button,
  ButtonGroup,
  closeDialog,
  DataStatus,
  Form,
  FormField,
  Label,
  Separator,
  Tooltip,
} from 'platform/components';
import {Box, Grid, GridItem, Heading, HStack, Icon, VStack} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {useRef} from 'react';

import {head, mergeAll} from 'ramda';
import {isArray, isNilOrEmpty} from 'ramda-adjunct';

import {useGetStorageLocationByIdApiQuery, usePatchStorageLocationV2Mutation} from '@dms/api/core';
import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';
import {
  catchUnhandledDataGridActions,
  DmsDataGrid,
  handleApiError,
  refreshDmsDatagrid,
  WAREHOUSE_GRID_CODES,
} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

const ARTICLES_GRID_CODE = WAREHOUSE_GRID_CODES.assignWarehouseArticleToStorageLocation;

const PREDEFINED_COLUMNS = ['warehouseId'] as const;

const TYPE_OPTIONS = [
  {label: i18n.t('entity.warehouse.labels.simpleLocationType'), value: 'simple'},
];

// The column value shape is defined by the datagrid service and differs between
// grids (plain id string, or an object carrying the id under `key`/`id`).
const getWarehouseIdFromColumn = (columnValue: unknown) =>
  match(columnValue)
    .with(Pattern.string, (warehouseId) => warehouseId)
    .with({key: Pattern.string}, ({key}) => key)
    .with({id: Pattern.string}, ({id}) => id)
    .otherwise(() => null);

interface StorageLocationDetailProps {
  storageLocationId: string;
  dialogId: string;
  spaceId: string;
  spaceName?: string;
  warehouseId?: string;
  isDisabled?: boolean;
}

interface StorageLocationEditForm {
  name: string;
  space: string;
  note: string;
  allowMultipleUsage: boolean;
}

export function StorageLocationDetail(props: StorageLocationDetailProps) {
  const navigate = useNavigate();

  const locationDetailQuery = useGetStorageLocationByIdApiQuery(
    {storageLocationId: props.storageLocationId},
    {refetchOnMountOrArgChange: true}
  );

  const [updateLocation, updateStatus] = usePatchStorageLocationV2Mutation();

  const spaceOptions = [{label: props.spaceName ?? '', value: props.spaceId}];

  const handleSubmit = async (formData: StorageLocationEditForm) => {
    const definition = locationDetailQuery.data?.definition;
    const updatedDefinition = definition
      ? {
          vectors: definition.vectors.map((vector) =>
            vector?.isSelected ? {...vector, symbol: formData.name} : vector
          ),
          separator: definition.separator ?? '',
        }
      : null;

    await updateLocation({
      storageLocationId: props.storageLocationId,
      body: {
        definition: updatedDefinition,
        note: formData.note,
        allowMultipleUsage: formData.allowMultipleUsage,
      },
    })
      .unwrap()
      .then(() => {
        refreshDmsDatagrid(WAREHOUSE_GRID_CODES.assignStorageLocationWarehouseSpace);
        closeDialog(props.dialogId);
      })
      .catch(handleApiError);
  };

  const queryModifier = (filter: QueryFilterObject) =>
    mergeAll([filter, {storageLocationId: props.storageLocationId}]);

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = (params) => {
    match(params.actionKey)
      .with('redirectDetail', () => {
        const id = isArray(params.rowId) ? head(params.rowId) : params.rowId;
        const warehouseId =
          getWarehouseIdFromColumn(
            params.getColumnData('warehouseId', {isOptional: true})?.value
          ) ?? props.warehouseId;

        if (isNilOrEmpty(id) || isNilOrEmpty(warehouseId)) {
          return;
        }

        closeDialog(props.dialogId);
        navigate(
          composePath(warehouseRoutes.articleDetailOverview, {
            params: {warehouseId, id},
            isSearchParamsPreserved: false,
          })
        );
      })
      .otherwise(() => catchUnhandledDataGridActions(params.actionKey));
  };

  const submitFormRef = useRef<(() => Promise<boolean>) | undefined>(undefined);

  return (
    <DataStatus isLoading={locationDetailQuery.isFetching} isError={locationDetailQuery.isError}>
      <VStack spacing={6}>
        <Form<StorageLocationEditForm>
          defaultValues={{
            name: locationDetailQuery.data?.location ?? '',
            space: props.spaceId,
            note: locationDetailQuery.data?.note ?? '',
            allowMultipleUsage: locationDetailQuery.data?.allowMultipleUsage ?? false,
          }}
          onSubmit={handleSubmit}
          isDisabled={props.isDisabled}
        >
          {(control) => {
            submitFormRef.current = control.submitForm;

            return (
              <VStack spacing={4}>
                <Grid columns={3}>
                  <GridItem>
                    <FormField
                      type="text"
                      control={control}
                      name="name"
                      label={i18n.t('general.labels.name')}
                    />
                  </GridItem>
                  <GridItem>
                    <FormField
                      type="choice"
                      control={control}
                      name="space"
                      label={i18n.t('entity.warehouse.labels.space')}
                      options={spaceOptions}
                      isNotClearable
                      isDisabled
                    />
                  </GridItem>
                  <GridItem>
                    <VStack>
                      <Label>{i18n.t('general.labels.type')}</Label>
                      <Box width={32}>
                        <Button
                          title={TYPE_OPTIONS[0].label}
                          variant="secondary"
                          isFullWidth={false}
                        />
                      </Box>
                    </VStack>
                  </GridItem>
                </Grid>
                <FormField
                  type="textarea"
                  control={control}
                  name="note"
                  label={i18n.t('general.labels.note')}
                  isResizable
                />
                <HStack spacing={1} align="center">
                  <FormField
                    type="checkbox"
                    control={control}
                    name="allowMultipleUsage"
                    label={i18n.t('entity.tireWarehouses.labels.unlimitedUsage')}
                  />
                  <Tooltip
                    description={i18n.t('entity.tireWarehouses.labels.unlimitedUsage.tooltip')}
                  >
                    <Icon value="action/help" color="text.secondary" size={4} tabIndex={0} />
                  </Tooltip>
                </HStack>
              </VStack>
            );
          }}
        </Form>
        <Separator />
        <Heading size={4}>{i18n.t('entity.warehouse.labels.locationUsage')}</Heading>
        <DmsDataGrid
          gridCode={ARTICLES_GRID_CODE}
          queryModifier={queryModifier}
          actionCallback={actionCallback}
          predefinedColumns={PREDEFINED_COLUMNS}
          autoHeight
        />
        <ButtonGroup align="right">
          {props.isDisabled ? (
            <Button
              title={i18n.t('general.actions.close')}
              onClick={() => closeDialog(props.dialogId)}
            />
          ) : (
            <>
              <Button
                title={i18n.t('general.actions.discard')}
                variant="secondary"
                onClick={() => closeDialog(props.dialogId)}
              />
              <Button
                title={i18n.t('general.actions.saveChanges')}
                onClick={() => submitFormRef.current?.()}
                isLoading={updateStatus.isLoading}
              />
            </>
          )}
        </ButtonGroup>
      </VStack>
    </DataStatus>
  );
}
